import { collection, doc, getDoc, getDocs, setDoc, query, orderBy, Timestamp } from 'firebase/firestore';
import { db } from '../config/firebase';
import { Assessment, AssessmentInput } from '../models/Assessment';

/**
 * Assessment snapshots — clients/{clientId}/assessments/{assessmentId}.
 * Immutable: created once, never updated. A new administration = a new doc.
 */
export class AssessmentService {
  private static instance: AssessmentService;
  private constructor() {}
  static getInstance(): AssessmentService {
    if (!AssessmentService.instance) AssessmentService.instance = new AssessmentService();
    return AssessmentService.instance;
  }

  private col(clientId: string) {
    return collection(db, 'clients', clientId, 'assessments');
  }

  async create(clientId: string, input: AssessmentInput): Promise<string> {
    const ref = doc(this.col(clientId));
    await setDoc(ref, {
      id: ref.id,
      clientId,
      assessmentName: input.assessmentName,
      assessmentVersion: input.assessmentVersion ?? null,
      administeredBy: input.administeredBy,
      administeredDate: Timestamp.fromDate(input.administeredDate),
      status: input.status ?? 'complete',
      domainScores: input.domainScores,
      overallScore: input.overallScore ?? null,
      overallMax: input.overallMax ?? null,
      clinicalNotes: input.clinicalNotes ?? '',
      recommendedActions: input.recommendedActions ?? null,
      createdAt: Timestamp.now(),
    });
    return ref.id;
  }

  async getByClient(clientId: string): Promise<Assessment[]> {
    const q = query(this.col(clientId), orderBy('administeredDate', 'desc'));
    const snap = await getDocs(q);
    return snap.docs.map((d) => this.map(clientId, d.id, d.data()));
  }

  async getAssessment(clientId: string, assessmentId: string): Promise<Assessment | null> {
    const snap = await getDoc(doc(this.col(clientId), assessmentId));
    if (!snap.exists()) return null;
    return this.map(clientId, snap.id, snap.data());
  }

  private toDate(v: any): Date {
    return v && typeof v.toDate === 'function' ? v.toDate() : new Date();
  }

  private map(clientId: string, id: string, data: any): Assessment {
    return {
      id,
      clientId: data.clientId || clientId,
      assessmentName: data.assessmentName || '',
      assessmentVersion: data.assessmentVersion ?? null,
      administeredBy: data.administeredBy || '',
      administeredDate: this.toDate(data.administeredDate),
      status: data.status || 'complete',
      domainScores: Array.isArray(data.domainScores) ? data.domainScores : [],
      overallScore: data.overallScore ?? null,
      overallMax: data.overallMax ?? null,
      clinicalNotes: data.clinicalNotes || '',
      recommendedActions: data.recommendedActions ?? null,
      createdAt: this.toDate(data.createdAt),
    };
  }
}

export const assessmentService = AssessmentService.getInstance();
